import React, { useCallback, useState } from "react";
import { useOutletContext } from "react-router-dom";
import usePrep from "../../components/usePrep";
import { ErrorBox, Loader } from "../../components/PortalBits";
import PdfViewer from "../../components/PdfViewer";

/* ---------- file type from file name / link / meta ("PDF · 2 MB") ---------- */
const TYPES = {
  pdf: { label: "PDF", color: "#e0282e" },
  doc: { label: "DOC", color: "#2b6fd6" },
  docx: { label: "DOCX", color: "#2b6fd6" },
  ppt: { label: "PPT", color: "#e36a1f" },
  pptx: { label: "PPTX", color: "#e36a1f" },
  xls: { label: "XLS", color: "#14945a" },
  xlsx: { label: "XLSX", color: "#14945a" },
  csv: { label: "CSV", color: "#14945a" },
  zip: { label: "ZIP", color: "#6b7280" },
  txt: { label: "TXT", color: "#6b7280" },
  png: { label: "PNG", color: "#7c3aed" },
  jpg: { label: "JPG", color: "#7c3aed" },
  jpeg: { label: "JPG", color: "#7c3aed" },
};

const extOf = (v) => {
  const m = String(v || "").split(/[?#]/)[0].match(/\.([a-z0-9]{2,5})$/i);
  return m ? m[1].toLowerCase() : "";
};

function fileType(doc) {
  const metaExt = String(doc.meta || "").trim().split(/[\s·|,-]/)[0].toLowerCase();
  const ext = extOf(doc.file_name) || metaExt;
  return TYPES[ext] ? { ext, ...TYPES[ext] } : { ext: "", label: "", color: "#6b7280" };
}

function Glyph({ ext }) {
  // white mark drawn on the coloured document
  if (ext === "pdf") {
    return (
      <path
        d="M11 25.5c1.6-2.9 3.4-6.5 4.5-9.6.9-2.6.6-4.6-.6-4.6-1.3 0-1.5 2.3-.6 4.9 1.1 3.1 3.6 6 6.4 7.3 2 .9 3.8.6 3.8-.5 0-1.2-2.3-1.6-5.4-1.1-3.5.6-7.1 2.1-8.6 3.6-1.2 1.2-.6 2.4.5 0z"
        fill="none"
        stroke="#fff"
        strokeWidth="1.7"
        strokeLinejoin="round"
      />
    );
  }
  const letter =
    ext.startsWith("xls") || ext === "csv" ? "X"
      : ext.startsWith("doc") ? "W"
        : ext.startsWith("ppt") ? "P"
          : ext === "zip" ? "Z"
            : ["png", "jpg", "jpeg"].includes(ext) ? "▲"
              : "≡";
  return (
    <text x="17" y="26" textAnchor="middle" fontSize="15" fontWeight="800" fill="#fff" fontFamily="Arial, sans-serif">
      {letter}
    </text>
  );
}

function FileIcon({ type }) {
  return (
    <svg className="res-file-icon" width="36" height="44" viewBox="0 0 34 42" aria-hidden="true">
      <path d="M4 0h18l12 12v26a4 4 0 0 1-4 4H4a4 4 0 0 1-4-4V4a4 4 0 0 1 4-4z" fill={type.color} />
      <path d="M22 0l12 12h-8a4 4 0 0 1-4-4z" fill="#fff" opacity=".35" />
      <Glyph ext={type.ext} />
    </svg>
  );
}

const CapIcon = () => (
  <svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M22 10 12 5 2 10l10 5 10-5z" />
    <path d="M6 12v5c0 1.1 2.7 3 6 3s6-1.9 6-3v-5" />
    <path d="M22 10v6" />
  </svg>
);

const ViewIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7S2 12 2 12z" />
    <circle cx="12" cy="12" r="3" />
  </svg>
);

function Resources() {
  const { loading, error, items, reload } = usePrep();
  const { user } = useOutletContext() || {};
  // View only: the PDF opens in the in-platform viewer (no download / print)
  const [viewing, setViewing] = useState(null);
  const closeViewer = useCallback(() => setViewing(null), []);
  const watermark = [user?.member_id, user?.email].filter(Boolean).join(" · ");

  // sirf wahi exams jinke resources available hain
  const groups = (items || []).filter((i) => (i.guides || []).length > 0);

  return (
    <div className="panel">
      <h2>Resources</h2>
      <div className="sub">Reference documents to study inside the portal (view only).</div>

      {loading && <Loader />}
      {!loading && error && <ErrorBox message={error} onRetry={reload} />}
      {!loading && !error && groups.length === 0 && (
        <div className="empty-state">No resources have been published for your certifications yet.</div>
      )}

      {!loading && !error && groups.length > 0 && (
        <div className="res-grid">
          {groups.map((g) => (
            <section key={g.exam_code} className="res-card">
              <header className="res-head">
                <div className="res-head-text">
                  <h3 className="res-exam-name">{g.full_name || g.name}</h3>
                  <div className="res-count">
                    {g.guides.length} {g.guides.length === 1 ? "resource" : "resources"}
                  </div>
                </div>
                <span className="res-cap">
                  <CapIcon color="#0891b2" />
                </span>
              </header>

              <div className="res-list">
                {g.guides.map((doc, i) => {
                  const type = fileType(doc);
                  const meta = doc.meta || type.label;
                  return (
                    <div key={`${g.exam_code}-${doc.title}-${i}`} className="res-row">
                      <FileIcon type={type} />
                      <div className="res-info">
                        <div className="res-title">{doc.title}</div>
                        {meta && <div className="res-meta">{meta}</div>}
                      </div>

                      {doc.viewable ? (
                        <button
                          type="button"
                          className="btn btn-sm btn-primary"
                          onClick={() => setViewing({ examCode: g.exam_code, index: doc.index, title: doc.title })}
                        >
                          <ViewIcon />
                          View
                        </button>
                      ) : (
                        <span className="res-btn res-btn-soon">Coming soon</span>
                      )}
                    </div>
                  );
                })}
              </div>
            </section>
          ))}
        </div>
      )}

      <PdfViewer
        open={!!viewing}
        title={viewing?.title || ""}
        examCode={viewing?.examCode}
        index={viewing?.index}
        watermark={watermark}
        onClose={closeViewer}
      />
    </div>
  );
}

export default Resources;
